// API wrappers + helpers for the booking module's CMS pages (Calendar /
// Services / Availability / Blocked times). Instants are UTC ISO strings;
// display formatting uses the site timezone from booking settings.
import type { ApiClient } from "./api";

export type BookingStatus = "pending" | "confirmed" | "cancelled";

export interface PortalBooking {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  starts_at: string;
  ends_at: string | null;
  party_size: number | null;
  notes: string | null;
  status: BookingStatus;
  service_id: string | null;
  event_id: string | null;
  occurrence_date: string | null;
  created_at: string;
  service_name?: string | null;
  event_title?: string | null;
}

/** What a calendar row was booked for: service name, event title, or a fallback. */
export function bookingItemLabel(booking: PortalBooking): string {
  return booking.service_name ?? booking.event_title ?? (booking.event_id ? "Event" : "Booking");
}

export interface PortalService {
  id: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  buffer_minutes: number;
  price_pence: number | null;
  deposit_required: number;
  capacity: number;
  active: number;
  sort_order: number;
}

/** 0 = Sunday .. 6 = Saturday (matches the API). */
export interface AvailabilityRule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface BlockedTime {
  id: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
}

export interface BookingSettings {
  timezone: string;
  slot_step_minutes: number;
  min_notice_minutes: number;
  max_advance_days: number;
  auto_confirm: number;
}

// --- API wrappers ---

export function fetchBookings(api: ApiClient, from: string, to: string) {
  const params = new URLSearchParams({ from, to });
  return api.request<{ bookings: PortalBooking[] }>(`/tenant/booking/bookings?${params}`);
}

export function updateBookingStatus(api: ApiClient, id: string, status: "confirmed" | "cancelled") {
  return api.request<{ booking: PortalBooking; previous_status: BookingStatus }>(
    `/tenant/booking/bookings/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ status }), headers: { "content-type": "application/json" } }
  );
}

export function fetchServices(api: ApiClient) {
  return api.request<{ services: PortalService[] }>("/tenant/booking/services");
}

export function createService(api: ApiClient, fields: Partial<PortalService>) {
  return api.request<{ service: PortalService }>("/tenant/booking/services", {
    method: "POST", body: JSON.stringify(fields), headers: { "content-type": "application/json" }
  });
}

export function updateService(api: ApiClient, id: string, fields: Partial<PortalService>) {
  return api.request<{ service: PortalService }>(`/tenant/booking/services/${encodeURIComponent(id)}`, {
    method: "PUT", body: JSON.stringify(fields), headers: { "content-type": "application/json" }
  });
}

export function deleteService(api: ApiClient, id: string) {
  return api.request<{ deleted: boolean }>(`/tenant/booking/services/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });
}

/** Per-service bookable hours (availability became per booking type). */
export function fetchServiceAvailability(api: ApiClient, serviceId: string) {
  return api.request<{ rules: AvailabilityRule[] }>(
    `/tenant/booking/services/${encodeURIComponent(serviceId)}/availability`
  );
}

export function saveServiceAvailability(api: ApiClient, serviceId: string, rules: AvailabilityRule[]) {
  return api.request<{ rules: AvailabilityRule[] }>(
    `/tenant/booking/services/${encodeURIComponent(serviceId)}/availability`,
    {
      method: "PUT",
      body: JSON.stringify({ rules: rules.map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time })) }),
      headers: { "content-type": "application/json" }
    }
  );
}

export function fetchBlocked(api: ApiClient) {
  return api.request<{ blocked: BlockedTime[] }>("/tenant/booking/blocked");
}

export function createBlocked(api: ApiClient, fields: { starts_at: string; ends_at: string; reason?: string | null }) {
  return api.request<{ blocked: BlockedTime }>("/tenant/booking/blocked", {
    method: "POST", body: JSON.stringify(fields), headers: { "content-type": "application/json" }
  });
}

export function deleteBlocked(api: ApiClient, id: string) {
  return api.request<{ deleted: boolean }>(`/tenant/booking/blocked/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });
}

export interface EventBookingConfig {
  event_id: string;
  enabled: number;
  capacity: number;
}

/** Per-event booking config (events-module integration); config null = never enabled. */
export function fetchEventBookingConfig(api: ApiClient, eventId: string) {
  return api.request<{ config: EventBookingConfig | null; booked_places: number }>(
    `/tenant/booking/events/${encodeURIComponent(eventId)}`
  );
}

export function saveEventBookingConfig(api: ApiClient, eventId: string, fields: { enabled: boolean; capacity: number }) {
  return api.request<{ config: EventBookingConfig }>(`/tenant/booking/events/${encodeURIComponent(eventId)}`, {
    method: "PUT", body: JSON.stringify(fields), headers: { "content-type": "application/json" }
  });
}

export function fetchBookingSettings(api: ApiClient) {
  return api.request<{ settings: BookingSettings }>("/tenant/booking/settings");
}

export function saveBookingSettings(api: ApiClient, fields: Partial<BookingSettings>) {
  return api.request<{ settings: BookingSettings }>("/tenant/booking/settings", {
    method: "PUT", body: JSON.stringify(fields), headers: { "content-type": "application/json" }
  });
}

// --- Display helpers ---
// (Weekday labels/order live in opening-hours.ts, shared with the hours grid.)

const DAY_MS = 86_400_000;

/** UTC midnight of the Monday of the week containing `date`. */
export function startOfWeekUtc(date: Date): Date {
  const utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const offset = (new Date(utc).getUTCDay() + 6) % 7;
  return new Date(utc - offset * DAY_MS);
}

export function addDaysUtc(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/** "Mon 20 Jul" in the site timezone. */
export function formatDayHeading(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone, weekday: "short", day: "numeric", month: "short"
  }).format(date);
}

/** "09:00" in the site timezone. */
export function formatTimeInZone(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23"
  }).format(new Date(iso));
}

/** "Mon 20 Jul, 09:00" in the site timezone. */
export function formatInstant(iso: string, timezone: string): string {
  return `${formatDayHeading(new Date(iso), timezone)}, ${formatTimeInZone(iso, timezone)}`;
}

/** The YYYY-MM-DD an instant falls on in the site timezone. */
export function dayKeyInZone(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"
  }).format(new Date(iso));
  return parts;
}

/** Pence → "£12.50" (whole pounds without decimals); null → em dash. */
export function formatServicePrice(pence: number | null | undefined): string {
  if (typeof pence !== "number") return "—";
  const pounds = pence / 100;
  return `£${pence % 100 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`;
}

/** "1h 30m" from minutes. */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours && rest) return `${hours}h ${rest}m`;
  if (hours) return `${hours}h`;
  return `${rest}m`;
}
